import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { DollarSign, AlertCircle } from "lucide-react";

interface ReviseOfferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposalId: string;
  rfpId: string;
  currentBid: number;
  supplierName: string;
  minDecrement?: number | null;
  onSuccess: () => void;
}

export const ReviseOfferDialog = ({
  open,
  onOpenChange,
  proposalId,
  rfpId,
  currentBid,
  supplierName,
  minDecrement,
  onSuccess,
}: ReviseOfferDialogProps) => {
  const { toast } = useToast();
  const [newAmount, setNewAmount] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const amount = parseFloat(newAmount.replace(/,/g, ""));
    
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid positive number",
        variant: "destructive",
      });
      return;
    }

    if (amount >= currentBid) {
      toast({
        title: "Invalid Amount",
        description: "Revised offer must be lower than the current bid",
        variant: "destructive",
      });
      return;
    }

    if (minDecrement && (currentBid - amount) < minDecrement) {
      toast({
        title: "Invalid Amount",
        description: `Minimum decrement is $${minDecrement.toLocaleString()}`,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.from("bids").insert({
        proposal_id: proposalId,
        rfp_id: rfpId,
        amount: amount,
        source: "AUCTION_BID",
        status: "requested",
      });

      if (error) throw error;

      toast({
        title: "Revised Offer Submitted",
        description: `New bid of $${amount.toLocaleString()} has been recorded for ${supplierName}`,
      });

      setNewAmount("");
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error("Error submitting revised offer:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit revised offer",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only numbers and commas
    const value = e.target.value.replace(/[^0-9,]/g, "");
    setNewAmount(value);
  };

  const suggestedAmount = minDecrement 
    ? currentBid - minDecrement 
    : currentBid - (currentBid * 0.05); // 5% reduction if no min decrement

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Submit Revised Offer</DialogTitle>
          <DialogDescription>
            Enter a new bid amount for {supplierName}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Current Bid Display */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Current Bid</p>
              <p className="text-2xl font-bold text-foreground">
                ${currentBid.toLocaleString()}
              </p>
            </div>

            {/* New Amount Input */}
            <div className="space-y-2">
              <Label htmlFor="amount">New Bid Amount</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="amount"
                  type="text"
                  placeholder="Enter amount"
                  value={newAmount}
                  onChange={handleAmountChange}
                  className="pl-9"
                  autoFocus
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Suggested: ${Math.round(suggestedAmount).toLocaleString()}
              </p>
            </div>

            {/* Validation Hints */}
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <AlertCircle className="h-4 w-4" />
                <span>Must be lower than current bid</span>
              </div>
              {minDecrement && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <AlertCircle className="h-4 w-4" />
                  <span>Minimum decrement: ${minDecrement.toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !newAmount}>
              {isSubmitting ? "Submitting..." : "Submit Revised Offer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
